import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Title, Text, Loader, Badge, ActionIcon, Select, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Kanban as KanbanIcon, Settings, Check, Trash2, X, Plus, ArrowRight, User, Users, ChevronDown } from 'lucide-react';
import { useTodoLists, useTodos, useUpdateTodo, useDeleteTodo, useCreateTodo } from '../../hooks/useTodos';
import { useUser } from '../../contexts/UserContext';
import { usePremiumFeatures } from '../../contexts/PremiumContext';
import { useKanbanConfig } from '../../hooks/useKanbanConfig';
import { kanbanApi } from '../../api/kanbanApi';
import type { UpdateTodoRequest } from '../../types/todo';
import { DndContext, DragEndEvent, DragOverEvent, DragStartEvent, MouseSensor, TouchSensor, useSensor, useSensors, DragOverlay, useDraggable, pointerWithin } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { reorderTodos } from '../../api/reorderApi';
import type { TodoItem as TodoItemType } from '../../types/todo';
import type { KanbanColumn as KanbanColumnType } from '../../types/kanban';
import { KanbanSettings } from './KanbanSettings';
import { EditTodoForm, TaskTitleInput } from '../EditTodoForm';
import { Modal } from '../Modal';
import { SharedHeader } from '../SharedHeader';
import { KanbanPreviewDemo } from '../KanbanPreviewDemo';
import './KanbanMode.css';
import './Swimlane.css';

// Add Task Input Component for Kanban Columns
const AddTaskInput: React.FC<{ 
  columnId: string;
  columnName: string;
  columnType?: 'start' | 'progress' | 'done';
  listId: number;
  assignee?: string; // Add assignee to props
  onTaskCreated?: () => void;
}> = ({ columnId, columnName, columnType, listId, assignee, onTaskCreated }) => {
  const { currentUser } = useUser();
  const [isVisible, setIsVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const createMutation = useCreateTodo();

  const handleShow = () => {
    setIsVisible(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleHide = () => {
    if (!inputValue.trim()) {
      setIsVisible(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleCreateTask();
    } else if (e.key === 'Escape') {
      setInputValue('');
      setIsVisible(false);
    }
  };

  const handleCreateTask = async () => {
    if (!inputValue.trim() || !currentUser?.email) return;

    const value = inputValue.trim();
    setInputValue(''); // Clear immediately for fast entry
    setIsVisible(false);

    createMutation.mutate({
      title: value,
      createdBy: currentUser.email,
      listId: listId,
      priority: 'Medium',
      status: columnId, // Set status on creation
      assignedTo: assignee === 'unassigned' ? undefined : assignee, // Assign to the swimlane user
    }, {
      onSuccess: async () => {
        try {
          // Refetch to get the real task with correct data
          await onTaskCreated?.();
        } catch (error) {
          console.error('Error in task creation flow:', error);
        }
      },
      onError: (error) => {
        console.error('Failed to create task:', error);
      }
    });
  };

  const placeholderText = useMemo(() => {
    const baseTexts = {
      'start': [
        'Ready to begin something new?',
        'What\'s next on the agenda?',
        'Time to kickstart a fresh idea...',
        'Let\'s get this party started! 🚀'
      ],
      'progress': [
        'Keep the momentum going...',
        'What\'s in the works?',
        'Another step forward...',
        'Building something amazing? ⚡'
      ],
      'done': [
        'One more to cross off the list?',
        'Almost there! What else?',
        'Ready to celebrate another win?',
        'Finishing strong! 🎯'
      ]
    } as const;

    const fallbackTexts = [
      `Add to ${columnName}...`,
      `New task for ${columnName}`,
      'What needs to be done?',
      'Got something in mind?'
    ];

    const texts = (columnType && baseTexts[columnType]) || fallbackTexts;
    // Use columnId as seed for consistent placeholder per column
    const seed = columnId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return texts[seed % texts.length];
  }, [columnName, columnType, columnId]);

  if (!isVisible) {
    return (
      <div 
        className="kanban-add-task-placeholder"
        onClick={handleShow}
        title="Click to add a new task"
      >
        <Plus size={14} />
        <span>{placeholderText}</span>
      </div>
    );
  }

  return (
    <div className="kanban-add-task-input">
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholderText}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleHide}
        maxLength={200}
        className="kanban-task-input"
      />
      <button 
        className="kanban-task-add-btn"
        onClick={handleCreateTask}
        disabled={!inputValue.trim()}
      >
        ↵
      </button>
    </div>
  );
};

// Sortable Kanban Card for regular columns with within-column sorting
const SortableKanbanCard: React.FC<{ 
  todo: TodoItemType; 
  onEdit?: (todo: TodoItemType) => void;
  columnId?: string; // Pass the actual column ID from parent
  assignee?: string;
}> = ({ todo, onEdit, columnId, assignee }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: `todo-${todo.id}`,
    disabled: todo.id < 0,
    data: {
      type: 'todo',
      todo: todo,
      columnId: columnId || 'todo',  // Use passed columnId for dynamic boards
      assignee: assignee || todo.assignedTo || 'unassigned'
    }
  });
  
  const isOptimistic = todo.id < 0;
  
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : isOptimistic ? 0.7 : 1,
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging && !isOptimistic && onEdit) {
      e.stopPropagation();
      onEdit(todo);
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...(isOptimistic ? {} : listeners)} 
      {...(isOptimistic ? {} : attributes)} 
      className={`kanban-card ${isOptimistic ? 'kanban-card-optimistic' : ''} ${todo.isCompleted ? 'kanban-card-completed' : ''}`}
      onClick={handleClick}
    >
      <div className="kanban-card-title">
        {todo.title}
        {isOptimistic && <span className="kanban-card-loading">⏳</span>}
      </div>
      <div className="kanban-card-meta">
        {todo.priority !== 'Medium' && <Badge size="xs" variant="light" color={todo.priority === 'High' ? 'red' : 'gray'}>{todo.priority}</Badge>}
        {todo.dueDate && <Badge size="xs" variant="outline" color="blue">{new Date(todo.dueDate).toLocaleDateString()}</Badge>}
        {/* Sub-task Progress Badge */}
        {todo.totalSubTasksCount !== undefined && todo.totalSubTasksCount > 0 && (
          <Tooltip
            label={`${todo.completedSubTasksCount} of ${todo.totalSubTasksCount} sub-tasks completed`}
            position="top"
            withArrow
          >
            <Badge
              variant="light"
              size="xs"
              color={todo.completedSubTasksCount === todo.totalSubTasksCount ? 'green' : 'gray'}
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '9px',
                opacity: 0.7
              }}
            >
              ☑ {todo.completedSubTasksCount}/{todo.totalSubTasksCount}
            </Badge>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// Draggable card for unmapped column - uses useDraggable with custom styling to prevent double overlay
const UnmappedKanbanCard: React.FC<{ 
  todo: TodoItemType; 
  onEdit?: (todo: TodoItemType) => void;
  assignee?: string;
}> = ({ todo, onEdit, assignee }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({ 
    id: `todo-${todo.id}`,
    disabled: todo.id < 0,
    data: {
      type: 'todo',
      todo: todo,
      columnId: '__unmapped__',  // Unmapped todos always use this ID
      assignee: assignee || todo.assignedTo || 'unassigned'
    }
  });
  
  const isOptimistic = todo.id < 0;
  
  const style: React.CSSProperties = {
    // When dragging, make the original completely invisible since DragOverlay handles the visual
    opacity: isDragging ? 0 : isOptimistic ? 0.7 : 1,
    // Don't apply transform to prevent double visual
    transform: 'none',
  };
  
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging && !isOptimistic && onEdit) {
      e.stopPropagation();
      onEdit(todo);
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...(isOptimistic ? {} : listeners)} 
      {...(isOptimistic ? {} : attributes)} 
      className={`kanban-card ${isOptimistic ? 'kanban-card-optimistic' : ''} ${todo.isCompleted ? 'kanban-card-completed' : ''}`}
      onClick={handleClick}
    >
      <div className="kanban-card-title">
        {todo.title}
        {isOptimistic && <span className="kanban-card-loading">⏳</span>}
      </div>
      <div className="kanban-card-meta">
        {todo.status && (
          <Badge size="xs" variant="filled" color="orange" title={`Status: ${todo.status}`}>
            {todo.status}
          </Badge>
        )}
        {todo.priority !== 'Medium' && <Badge size="xs" variant="light" color={todo.priority === 'High' ? 'red' : 'gray'}>{todo.priority}</Badge>}
        {todo.dueDate && <Badge size="xs" variant="outline" color="blue">{new Date(todo.dueDate).toLocaleDateString()}</Badge>}
        {/* Sub-task Progress Badge */}
        {todo.totalSubTasksCount !== undefined && todo.totalSubTasksCount > 0 && (
          <Tooltip
            label={`${todo.completedSubTasksCount} of ${todo.totalSubTasksCount} sub-tasks completed`}
            position="top"
            withArrow
          >
            <Badge
              variant="light"
              size="xs"
              color={todo.completedSubTasksCount === todo.totalSubTasksCount ? 'green' : 'gray'}
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '9px',
                opacity: 0.7
              }}
            >
              ☑ {todo.completedSubTasksCount}/{todo.totalSubTasksCount}
            </Badge>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// Sortable Kanban Column with proper drag and drop support
const KanbanColumn: React.FC<{
  id: string;
  name: string;
  color?: string;
  todos: TodoItemType[];
  isUnmapped?: boolean;
  columnType?: 'start' | 'progress' | 'done';
  onEditTodo?: (todo: TodoItemType) => void;
  listId?: number;
  onTaskCreated?: () => void;
  onOptimisticAdd?: (tempTodo: TodoItemType) => void;
  onOptimisticRemove?: (tempId: number) => void;
  assignee?: string;
}> = ({ id, name, color, todos, isUnmapped, columnType, onEditTodo, listId, onTaskCreated, assignee }) => {
  const { setNodeRef } = useDroppable({ 
    id: `${id}-${assignee || 'no-assignee'}`, 
    data: {
      type: 'column',
      columnId: id,
      assignee: assignee,
      accepts: ['todo']
    }
  });
  
  // Sort todos by rank within the column
  const sortedTodos = [...todos].sort((a, b) => a.rank - b.rank);
  const todoIds = sortedTodos.map(t => `todo-${t.id}`);
  
  // Get column type icon and styling
  const getColumnTypeIndicator = () => {
    switch (columnType) {
      case 'start':
        return <Badge size="xs" variant="light" color="green" title="Start Column - Default for new todos">START</Badge>;
      case 'done':
        return <Badge size="xs" variant="light" color="blue" title="Done Column - Marks todos as complete">DONE</Badge>;
      default:
        return null;
    }
  };

  // Get background style for special column types
  const getColumnBackgroundStyle = () => {
    if (columnType === 'start' || columnType === 'done') {
      return {
        // Use CSS custom properties that will work in both light and dark modes
        background: `linear-gradient(135deg, var(--app-surface) 0%, var(--app-hover) 100%)`,
        borderColor: color || 'var(--app-border)',
        borderWidth: '2px'
      };
    }
    return {};
  };
  
  return (
    <div 
      className="kanban-column" 
      style={getColumnBackgroundStyle()}
    >
      <div className="kanban-column-header">
        <div className="kanban-column-dot" style={{ backgroundColor: color || 'var(--app-text-muted)' }} />
        <Text fw={600} c="var(--app-text-primary)">{name}</Text>
        <Badge size="sm" variant="light">{todos.length}</Badge>
        {getColumnTypeIndicator()}
        {isUnmapped && (
          <Badge size="xs" variant="filled" color="orange" title="Contains todos with statuses not matching any configured column">
            !
          </Badge>
        )}
      </div>
      <div ref={setNodeRef} className="kanban-column-body">
        <SortableContext items={todoIds} strategy={verticalListSortingStrategy}>
          {sortedTodos.map(t => (
            <SortableKanbanCard key={t.id} todo={t} onEdit={onEditTodo} columnId={id} assignee={assignee} />
          ))}
        </SortableContext>
        
        {/* Add Task Input - Only show for normal columns, not unmapped */}
        {!isUnmapped && listId && (
          <AddTaskInput
            columnId={id}
            columnName={name}
            columnType={columnType}
            listId={listId}
            assignee={assignee}
            onTaskCreated={onTaskCreated}
          />
        )}
      </div>
    </div>
  );
};


// Unmapped Column (special column for todos with unmapped statuses)
const UnmappedColumn: React.FC<{ 
  todos: TodoItemType[];
  onEditTodo?: (todo: TodoItemType) => void;
  onMoveAllToStart?: () => void;
  startColumnName?: string;
  assignee?: string;
}> = ({ todos, onEditTodo, onMoveAllToStart, startColumnName, assignee }) => {
  const { setNodeRef } = useDroppable({ 
    id: `__unmapped__-${assignee || 'no-assignee'}`,
    data: {
      type: 'column',
      columnId: '__unmapped__',
      assignee: assignee
    }
  });
  
  // Sort todos by rank within the unmapped column
  const sortedTodos = [...todos].sort((a, b) => a.rank - b.rank);
  
  return (
    <div className="kanban-column">
      <div className="kanban-column-header">
        <div className="kanban-column-dot" style={{ backgroundColor: 'var(--app-primary)' }} />
        <Text fw={600} c="var(--app-text-primary)">Unmapped</Text>
        <Badge size="sm" variant="light">{todos.length}</Badge>
        <Badge size="xs" variant="filled" color="orange" title="Contains todos with statuses not matching any configured column">
          !
        </Badge>
        {onMoveAllToStart && todos.length > 0 && (
          <ActionIcon
            size="sm"
            variant="light"
            color="blue"
            onClick={onMoveAllToStart}
            title={`Move all tasks into ${startColumnName || 'the start column'}`}
          >
            <ArrowRight size={12} />
          </ActionIcon>
        )}
      </div>
      <Text size="xs" c="var(--app-text-secondary)" mb="xs" px="sm">
        Tasks with statuses not in current board setup. Drag to reassign.
      </Text>
      <div ref={setNodeRef} className="kanban-column-body">
        {sortedTodos.map(t => (
          <UnmappedKanbanCard key={t.id} todo={t} onEdit={onEditTodo} assignee={assignee} />
        ))}
      </div>
    </div>
  );
};

const Swimlane: React.FC<{
  assignee: string;
  todos: TodoItemType[];
  columns: KanbanColumnType[];
  unmappedTodos: TodoItemType[];
  onEditTodo: (todo: TodoItemType) => void;
  listId: number | null;
  refetchTodos: () => void;
  refetchConfig: () => void;
  onMoveAllToStart?: () => void;
  startColumnName?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}> = ({ assignee, todos, columns, unmappedTodos, onEditTodo, listId, refetchTodos, refetchConfig, onMoveAllToStart, startColumnName, isCollapsed, onToggleCollapse }) => {
    const todosByColumn = useMemo(() => {
        const by: Record<string, TodoItemType[]> = {};
        const configuredColumnIds = new Set(columns.map(c => c.id));
        
        columns.forEach(c => { by[c.id] = []; });
        
        for (const t of todos) {
            const status = t.status || 'todo';
            if (configuredColumnIds.has(status)) {
                by[status].push(t);
            }
        }
        
        Object.keys(by).forEach(columnId => {
            by[columnId].sort((a, b) => a.rank - b.rank);
        });
        
        return by;
    }, [todos, columns]);

    const getInitials = (email: string) => {
        if (!email || email === 'unassigned') return '?';
        return email.charAt(0).toUpperCase();
    }

    // Simple hash function to get a color index from an email string
    const getColorIndex = (email: string) => {
        if (!email || email === 'unassigned') return 0;
        let hash = 0;
        for (let i = 0; i < email.length; i++) {
            hash = email.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash % 8); // 8 accent colors available
    }

    return (
        <div className={`kanban-swimlane ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="kanban-swimlane-header" onClick={onToggleCollapse}>
                <ChevronDown size={16} className="swimlane-collapse-icon" />
                <div className={`swimlane-avatar accent-bg-${getColorIndex(assignee)}`}>
                    {assignee === 'unassigned' ? <User size={16} /> : <span>{getInitials(assignee)}</span>}
                </div>
                <Text fw={600} c="var(--app-text-primary)">{assignee === 'unassigned' ? 'Unassigned' : assignee}</Text>
                <Badge size="sm" variant="light">{todos.length + unmappedTodos.length}</Badge>
            </div>
            <div className="kanban-swimlane-columns">
                {unmappedTodos.length > 0 && (
                    <UnmappedColumn 
                        todos={unmappedTodos}
                        onEditTodo={onEditTodo}
                        onMoveAllToStart={onMoveAllToStart}
                        startColumnName={startColumnName}
                        assignee={assignee}
                    />
                )}
                {columns.map(col => (
                    <KanbanColumn
                        key={`${assignee}-${col.id}`}
                        id={col.id}
                        name={col.name}
                        color={col.color}
                        todos={todosByColumn[col.id] || []}
                        columnType={col.type}
                        onEditTodo={onEditTodo}
                        listId={listId || undefined}
                        onTaskCreated={async () => {
                            await Promise.all([
                                refetchTodos(),
                                refetchConfig()
                            ]);
                        }}
                        assignee={assignee}
                      />
                ))}
            </div>
        </div>
    );
};

export const KanbanMode: React.FC = () => {
  const { currentUser } = useUser();
  const { isPremium, isLoading: premiumLoading } = usePremiumFeatures();
  const { data: lists = [], isLoading: listsLoading } = useTodoLists();
  const { data: allTodos = [], isLoading: todosLoading, refetch: refetchTodos } = useTodos();
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoItemType | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateTodoRequest | null>(null);
  const [draggedTodo, setDraggedTodo] = useState<TodoItemType | null>(null);
  const [swimlaneMode, setSwimlaneMode] = useState<'none' | 'assignee'>('none');
  const [collapsedSwimlanes, setCollapsedSwimlanes] = useState<Record<string, boolean>>({});
  const [, setDraggedOverContainer] = useState<string | null>(null);
  // Removed originalColumnId state - no longer needed with current logic
  const [optimisticReordering, setOptimisticReordering] = useState<{
    sourceContainer: string;
    targetContainer: string;
    activeId: string;
    overId?: string;
    todos: Record<string, TodoItemType[]>;
  } | null>(null);
  const { data: config, isLoading: configLoading, refetch } = useKanbanConfig(selectedListId || undefined);

  // Mutation hooks for updating and deleting todos
  const updateTodo = useUpdateTodo();
  const deleteTodo = useDeleteTodo();

  const toggleSwimlaneCollapse = (assignee: string) => {
    setCollapsedSwimlanes(prev => ({
        ...prev,
        [assignee]: !prev[assignee]
    }));
  };

  useEffect(() => {
    if (!selectedListId && lists.length > 0) {
      setSelectedListId(lists[0].id);
    }
  }, [lists, selectedListId]);

  const sensors = useSensors(
    useSensor(MouseSensor, { 
      activationConstraint: { 
        distance: 8 
      } 
    }),
    useSensor(TouchSensor, { 
      activationConstraint: { 
        delay: 200, 
        tolerance: 8 
      } 
    })
  );

  // Handlers for optimistic updates - REMOVED, now handled by useCreateTodo
  
  const todosForList = useMemo(() => {
    return (allTodos || []).filter(t => t.listId === selectedListId);
  }, [allTodos, selectedListId]);

  const swimlaneData = useMemo(() => {
    if (swimlaneMode !== 'assignee') {
      return null;
    }

    const byAssignee: Record<string, TodoItemType[]> = {
      'unassigned': []
    };

    for (const todo of todosForList) {
      const assignee = todo.assignedTo || 'unassigned';
      if (!byAssignee[assignee]) {
        byAssignee[assignee] = [];
      }
      byAssignee[assignee].push(todo);
    }

    return byAssignee;
  }, [todosForList, swimlaneMode]);

  const todosByColumn = useMemo(() => {
    // If we have optimistic reordering, use that instead of the real data
    if (optimisticReordering) {
      return optimisticReordering.todos;
    }

    const by: Record<string, TodoItemType[]> = {};
    const cols = (config?.columns || []) as KanbanColumnType[];
    const configuredColumnIds = new Set(cols.map(c => c.id));
    
    // Initialize configured columns
    cols.forEach((c: KanbanColumnType) => { by[c.id] = []; });
    
    // Add unmapped column for todos with statuses not in configured columns
    by['__unmapped__'] = [];
    
    for (const t of todosForList) {
      const status = t.status || 'todo';
      // Clean up debug logging for production
      if (configuredColumnIds.has(status)) {
        // Todo status matches a configured column
        by[status].push(t);
      } else {
        // Todo status doesn't match any configured column - add to unmapped
        by['__unmapped__'].push(t);
      }
    }
    
    // Sort by rank within each column
    Object.keys(by).forEach(columnId => {
      by[columnId].sort((a, b) => a.rank - b.rank);
    });
    
    return by;
  }, [todosForList, config, optimisticReordering]);

  const handleEditTodo = (todo: TodoItemType) => {
    setEditingTodo(todo);
  };

  const handleCloseEdit = () => {
  setEditingTodo(null);
  setEditFormData(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);
    // Clean drag start logging - functionality working correctly
    
    if (activeId.startsWith('todo-')) {
      const todoId = parseInt(activeId.replace('todo-', ''), 10);
      const todo = todosForList?.find(t => t.id === todoId);
      
      setDraggedTodo(todo || null);
      
      // Clear any previous optimistic reordering
      setOptimisticReordering(null);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      console.log('No over target');
      setDraggedOverContainer(null);
      setOptimisticReordering(null);
      return;
    }

    // Clean debug logging - drag over working correctly

    const activeId = String(active.id);
    const overId = String(over.id);
    const activeData = active.data.current;
    const overData = over.data.current;


    if (!activeId.startsWith('todo-')) {
      setDraggedOverContainer(null);
      return;
    }

    const todoId = parseInt(activeId.replace('todo-', ''), 10);
    const draggedTodo = todosForList?.find(t => t.id === todoId);
    if (!draggedTodo) return;

    // For dynamic boards, get the active column from the drag data instead of todo status
    const activeColumnId = activeData?.columnId || '__unmapped__';
    let targetColumnId = activeColumnId;
    let insertIndex = 0;

    // Determine target column and insertion position
    if (overData?.type === 'column') {
      // Dropping on empty column area
      targetColumnId = overData.columnId;
      insertIndex = 0;
    } else if (overData?.type === 'todo') {
      // Dropping on another todo
      targetColumnId = overData.columnId;
      const targetTodos = todosByColumn[targetColumnId] || [];
      const overTodoId = parseInt(overId.replace('todo-', ''), 10);
      const overIndex = targetTodos.findIndex(t => t.id === overTodoId);
      
      // Replace the target item (replacement semantics)
      if (overIndex !== -1) {
        insertIndex = overIndex; // Replace target
      } else {
        insertIndex = targetTodos.length; // Insert at end if target not found
      }
    } else if (overId === '__unmapped__') {
      // Dropping on unmapped column area
      targetColumnId = '__unmapped__';
      insertIndex = 0;
    } else {
      // Fallback: assume it's a column ID
      const column = config?.columns?.find((col: KanbanColumnType) => col.id === overId);
      if (column) {
        targetColumnId = overId;
        insertIndex = 0;
      }
    }


    // Only create optimistic updates for cross-column moves
    if (activeColumnId !== targetColumnId) {
      
      // Use clean base state from real todos (not optimistic data) to prevent duplicates
      const baseTodosByColumn: Record<string, TodoItemType[]> = {};
      const cols = (config?.columns || []) as KanbanColumnType[];
      const configuredColumnIds = new Set(cols.map(c => c.id));
      
      // Initialize all columns
      cols.forEach((c: KanbanColumnType) => { baseTodosByColumn[c.id] = []; });
      baseTodosByColumn['__unmapped__'] = [];
      
      // Populate with real todos only (exclude optimistic todos)
      if (todosForList && Array.isArray(todosForList)) {
        for (const t of todosForList) {
          if (t.id > 0) { // Only real todos, exclude optimistic ones
            const status = t.status || 'todo';
            if (configuredColumnIds.has(status)) {
              baseTodosByColumn[status].push(t);
            } else {
              baseTodosByColumn['__unmapped__'].push(t);
            }
          }
        }
      }
      
      // Sort by rank within each column
      Object.keys(baseTodosByColumn).forEach(columnId => {
        baseTodosByColumn[columnId].sort((a, b) => a.rank - b.rank);
      });
      
      // Create working copy
      const newTodosByColumn = { ...baseTodosByColumn };
      Object.keys(newTodosByColumn).forEach(key => {
        newTodosByColumn[key] = [...newTodosByColumn[key]];
      });

      // Remove from source column
      if (newTodosByColumn[activeColumnId] && Array.isArray(newTodosByColumn[activeColumnId])) {
        newTodosByColumn[activeColumnId] = newTodosByColumn[activeColumnId].filter(t => t.id !== todoId);
      }

      // Create dragged todo with new status
      const draggedTodoWithNewStatus = {
        ...draggedTodo,
        status: targetColumnId
      };

      // Insert into target column at calculated position
      if (!newTodosByColumn[targetColumnId] || !Array.isArray(newTodosByColumn[targetColumnId])) {
        newTodosByColumn[targetColumnId] = [];
      }
      newTodosByColumn[targetColumnId].splice(insertIndex, 0, draggedTodoWithNewStatus);

      // Update optimistic state
      setOptimisticReordering({
        sourceContainer: activeColumnId,
        targetContainer: targetColumnId,
        activeId: activeId,
        overId: overId,
        todos: newTodosByColumn
      });
    }

    setDraggedOverContainer(targetColumnId);
  };


  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    // Clean debug logging - drag end working correctly
    
    setDraggedTodo(null);
    setDraggedOverContainer(null);
    
    if (!over || !selectedListId || !currentUser?.email) {
      console.log('🎯 DRAG END - Early return:', { over: !!over, selectedListId, email: !!currentUser?.email });
      setOptimisticReordering(null);
      return;
    }
    
    const activeId = String(active.id);
    const overId = String(over.id);
    
    if (!activeId.startsWith('todo-')) {
      setOptimisticReordering(null);
      return;
    }
    
    // Handle dropping back on unmapped column - just clear optimistic state and return for same-column drops
    if (overId === '__unmapped__') {
      // For unmapped items being dropped back on unmapped, just clear optimistic state
      const todoId = parseInt(activeId.replace('todo-', ''), 10);
      const originalTodo = todosForList?.find(t => t.id === todoId);
      const originalIsUnmapped = !originalTodo?.status || !config?.columns?.some((col: KanbanColumnType) => col.id === originalTodo.status);
      
      if (originalIsUnmapped) {
        setOptimisticReordering(null);
        return;
      }
    }
    
    // Allow drag processing to continue even if activeId === overId for cross-column moves
    
    try {
      const todoId = parseInt(activeId.replace('todo-', ''), 10);
      const originalTodo = todosForList?.find(t => t.id === todoId);
      if (!originalTodo) {
        setOptimisticReordering(null);
        return;
      }
      
      const overData = over.data.current;
      const activeData = active.data.current;

      // For dynamic boards, determine the actual source column for this todo
      // If the todo's status doesn't match any configured column, it's in unmapped
      let activeColumnId = '__unmapped__';
      if (originalTodo?.status) {
        const configuredColumnIds = new Set((config?.columns || []).map((c: KanbanColumnType) => c.id));
        if (configuredColumnIds.has(originalTodo.status)) {
          activeColumnId = originalTodo.status;
        }
      }
      
      let targetColumnId = activeColumnId;
      let targetAssignee = activeData?.assignee;
      
      // Determine target column and assignee
      if (overData?.type === 'column') {
        targetColumnId = overData.columnId;
        targetAssignee = overData.assignee;
      } else if (overData?.type === 'todo') {
        targetColumnId = overData.columnId;
        targetAssignee = overData.assignee;
      }

      const sourceAssignee = activeData?.assignee;
      const isSwimlaneMove = swimlaneMode === 'assignee' && sourceAssignee !== targetAssignee;
      
      // Clean debug logging - column detection working correctly
      
      if (activeColumnId === targetColumnId && !isSwimlaneMove) {
        // Same-column move - use arrayMove pattern
        
        const currentColumnTodos = todosByColumn[activeColumnId] || [];
        const oldIndex = currentColumnTodos.findIndex(t => t.id === todoId);
        
        if (overId.startsWith('todo-')) {
          const overTodoId = parseInt(overId.replace('todo-', ''), 10);
          const newIndex = currentColumnTodos.findIndex(t => t.id === overTodoId);
          
          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            
            // Use arrayMove to reorder within column
            const reorderedTodos = arrayMove(currentColumnTodos, oldIndex, newIndex);
            
            // Update todosByColumn with reordered todos for this column
            const updatedTodosByColumn = { ...todosByColumn };
            updatedTodosByColumn[activeColumnId] = reorderedTodos;
            
            // Create final order for backend
            const allTodosInNewOrder: TodoItemType[] = [];
            const columnOrder = (config?.columns || []) as KanbanColumnType[];
            
            columnOrder.forEach(col => {
              const columnTodos = updatedTodosByColumn[col.id] || [];
              allTodosInNewOrder.push(...columnTodos);
            });
            
            const unmappedTodos = updatedTodosByColumn['__unmapped__'] || [];
            allTodosInNewOrder.push(...unmappedTodos);
            
            // Send reorder to backend (this is separate from todo mutations)
            const allOrderedIds = allTodosInNewOrder.map(t => t.id).filter(id => id > 0);
            
            if (allOrderedIds.length > 0) {
              await reorderTodos(allOrderedIds, currentUser.email, selectedListId);
              // Refetch todos to reflect new order (reorder API doesn't update TanStack cache)
              await refetchTodos();
            }
            
            // Clear optimistic state after same-column reorder completes
            setOptimisticReordering(null);
          }
        } else {
          // No reordering needed, just clear optimistic state
          setOptimisticReordering(null);
        }
      } else {
        // Cross-column move
        // For cross-column moves, we need to work with the original data (not optimistic data)
        // because optimistic updates have already moved the todo during drag-over
        const baseTodosByColumn: Record<string, TodoItemType[]> = {};
        const cols = (config?.columns || []) as KanbanColumnType[];
        const configuredColumnIds = new Set(cols.map(c => c.id));
        
        // Initialize all columns with original data (not optimistic)
        cols.forEach((c: KanbanColumnType) => { baseTodosByColumn[c.id] = []; });
        baseTodosByColumn['__unmapped__'] = [];
        
        // Populate with real todos only (exclude optimistic todos)
        if (todosForList && Array.isArray(todosForList)) {
          const realTodos = todosForList.filter(t => t.id > 0);
          for (const t of realTodos) {
            const status = t.status || 'todo';
            if (configuredColumnIds.has(status)) {
              baseTodosByColumn[status].push(t);
            } else {
              baseTodosByColumn['__unmapped__'].push(t);
            }
          }
        }
        
        // Sort by rank within each column
        Object.keys(baseTodosByColumn).forEach(columnId => {
          baseTodosByColumn[columnId].sort((a, b) => a.rank - b.rank);
        });
        
        // Handle cross-column move
        const sourceColumnTodos = [...(baseTodosByColumn[activeColumnId] || [])];
        const targetColumnTodos = [...(baseTodosByColumn[targetColumnId] || [])];
        
        const draggedIndex = sourceColumnTodos.findIndex(t => t.id === todoId);
        
        if (draggedIndex !== -1) {
          // Remove from source
          const [draggedTodo] = sourceColumnTodos.splice(draggedIndex, 1);
          
          // Update status for target column
          const updatedTodo = {
            ...draggedTodo,
            status: targetColumnId
          };
          
          // For cross-column moves, determine target position:
          // If dropping on a todo, insert at that position
          // If dropping on column area, append to end
          let targetIndex = targetColumnTodos.length; // Default to end
          
          if (overId.startsWith('todo-') && overData?.type === 'todo') {
            // Dropping on another todo - insert at that position
            const overTodoId = parseInt(overId.replace('todo-', ''), 10);
            const overIndex = targetColumnTodos.findIndex(t => t.id === overTodoId);
            if (overIndex !== -1) {
              targetIndex = overIndex; // Insert at target's position
            }
          }
          
          targetColumnTodos.splice(targetIndex, 0, updatedTodo);
          
          // Update all columns with the new arrangement
          const updatedTodosByColumn = { ...baseTodosByColumn };
          updatedTodosByColumn[activeColumnId] = sourceColumnTodos;
          updatedTodosByColumn[targetColumnId] = targetColumnTodos;
          
          // Create final order for backend
          const allTodosInNewOrder: TodoItemType[] = [];
          const columnOrder = (config?.columns || []) as KanbanColumnType[];
          
          columnOrder.forEach(col => {
            const columnTodos = updatedTodosByColumn[col.id] || [];
            allTodosInNewOrder.push(...columnTodos);
          });
          
          const unmappedTodos = updatedTodosByColumn['__unmapped__'] || [];
          allTodosInNewOrder.push(...unmappedTodos);
          
          // Send reorder to backend
          const allOrderedIds = allTodosInNewOrder.map(t => t.id).filter(id => id > 0);
          
          if (allOrderedIds.length > 0) {
            await reorderTodos(allOrderedIds, currentUser.email, selectedListId);
            // Refetch todos to reflect new order (reorder API doesn't update TanStack cache)
            await refetchTodos();
          }
          
          // Clear optimistic state after cross-column operations complete
          setOptimisticReordering(null);
          
          // Use the updateTodo mutation which has proper optimistic updates
          const targetColumn = config?.columns?.find((col: KanbanColumnType) => col.id === targetColumnId);
          const updateData: UpdateTodoRequest = {
            status: targetColumnId,
          };

          if (isSwimlaneMove) {
            updateData.assignedTo = targetAssignee === 'unassigned' ? null : targetAssignee;
          }
          
          // Handle completion for done columns
          if (targetColumn?.type === 'done' && !originalTodo.isCompleted) {
            updateData.isCompleted = true;
          } else if (targetColumn?.type !== 'done' && originalTodo.isCompleted) {
            updateData.isCompleted = false;
          }
          
          // Use mutation with built-in optimistic updates - this will update the cache immediately
          await new Promise<void>((resolve, reject) => {
            updateTodo.mutate({
              id: todoId,
              data: updateData
            }, {
              onSuccess: () => resolve(),
              onError: (error) => reject(error)
            });
          });
          
          // Clear optimistic state after mutation completes (cross-column without reorder)
          setOptimisticReordering(null);
        }
      }
      
      // Keep optimistic state until operations complete to prevent visual snap-back
      
    } catch (e) {
      console.error('Failed to move task:', e);
      // Clear optimistic state immediately on error
      setOptimisticReordering(null);
      // The updateTodo mutation will handle cache rollback automatically
    }
  };

  // Helper function to get the name of the start column (or leftmost column)
  const getStartColumnName = () => {
    if (!config?.columns?.length) return 'the start column';
    
    // Find the start column type first
    const startColumn = config.columns.find((col: KanbanColumnType) => col.type === 'start');
    if (startColumn) return startColumn.name;
    
    // Otherwise return the first (leftmost) column
    return config.columns[0]?.name || 'the start column';
  };

  // Function to move all unmapped tasks to the start column
  const handleMoveAllToStart = async () => {
    if (!config?.columns?.length || !currentUser?.email || !selectedListId) return;
    
    // Get fresh unmapped todos from the actual todosForList to avoid using stale optimistic data
    const unmappedTodos = (todosForList || []).filter(todo => {
      if (!todo.status) return true; // null status = unmapped
      const configuredColumnIds = new Set((config?.columns || []).map((c: KanbanColumnType) => c.id));
      return !configuredColumnIds.has(todo.status); // status not in configured columns = unmapped
    });
    
    if (unmappedTodos.length === 0) return;
    
    // Find the start column (or use first column)
    const startColumn = config.columns.find((col: KanbanColumnType) => col.type === 'start') || config.columns[0];
    if (!startColumn) return;
    
    const taskCount = unmappedTodos.length;
    const taskWord = taskCount === 1 ? 'task' : 'tasks';
    
    try {
      // Show loading notification
      const loadingNotificationId = notifications.show({
        title: 'Moving tasks...',
        message: `Moving ${taskCount} ${taskWord} to ${startColumn.name}`,
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
      
      // Clear any optimistic state first to prevent conflicts
      setOptimisticReordering(null);
      
      // Update each unmapped todo's status to the start column
      const updatePromises = unmappedTodos.map(todo => 
        kanbanApi.updateTodoStatus(todo.id, startColumn.id, currentUser.email)
      );
      
      await Promise.all(updatePromises);
      
      // Force multiple refreshes to ensure data consistency
      await Promise.all([refetchTodos(), refetch()]);
      
      // Small delay to ensure UI updates
      setTimeout(async () => {
        await Promise.all([refetchTodos(), refetch()]);
      }, 100);
      
      // Hide loading notification and show success
      notifications.hide(loadingNotificationId);
      notifications.show({
        title: 'Tasks moved successfully!',
        message: `Moved ${taskCount} ${taskWord} to ${startColumn.name}`,
        color: 'green',
        autoClose: 3000,
      });
      
    } catch (error) {
      console.error('Failed to move all unmapped tasks to start column:', error);
      
      // Clear optimistic state on error too
      setOptimisticReordering(null);
      
      // Show error notification
      notifications.show({
        title: 'Error moving tasks',
        message: `Failed to move ${taskCount} ${taskWord}. Please try again.`,
        color: 'red',
        autoClose: 5000,
      });
    }
  };

  // Handle upgrade button click
  const handleUpgradeClick = () => {
  // Navigate to Subscription Plan tab in Settings (same tab)
  window.location.href = '/settings?tab=plan';
  };

  // Show loading state while premium status is being determined
  if (premiumLoading) {
    return (
      <div style={{ 
        height: '100vh',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--app-bg)'
      }}>
        <Text size="lg" style={{ color: 'var(--app-text-primary)' }}>
          Loading Kanban Board...
        </Text>
      </div>
    );
  }

  // Show demo for non-premium users
  if (!isPremium) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'var(--app-bg)'
      }}>
        {/* Demo Content */}
        <div style={{ 
          flex: 1, 
          padding: '20px', 
          paddingBottom: '40px', // Extra bottom padding for mobile
          overflowY: 'auto',
          // Ensure mobile scrolling works
          WebkitOverflowScrolling: 'touch'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <Text size="xl" fw={600} style={{ color: 'var(--app-text-primary)' }}>
                🚀 Experience Kanban Board Magic
              </Text>
              <Text size="sm" style={{ color: 'var(--app-text-secondary)' }}>
                Try our interactive demo and upgrade for unlimited Kanban power
              </Text>
            </div>
            
            <KanbanPreviewDemo onUpgradeClick={handleUpgradeClick} />
          </div>
        </div>
      </div>
    );
  }

  // Separate loading conditions to handle empty state properly
  const isInitialLoading = listsLoading || todosLoading;
  const hasNoLists = !listsLoading && lists.length === 0;
  const loading = isInitialLoading || (configLoading && selectedListId);

  return (
    <div className="kanban-mode-container">
      <SharedHeader
        title="Kanban Board"
        icon={<KanbanIcon size={20} />}
        selectedListId={selectedListId}
        onListChange={setSelectedListId}
        additionalActions={
          <>
            <Select
                size="xs"
                value={swimlaneMode}
                onChange={(value) => setSwimlaneMode(value as 'none' | 'assignee')}
                data={[
                    { value: 'none', label: 'No Grouping' },
                    { value: 'assignee', label: 'Group by Assignee' },
                ]}
                leftSection={<Users size={14} />}
                style={{ width: 160 }}
            />
            {selectedListId && (
              <ActionIcon
                variant="light"
                onClick={() => setShowSettings(true)}
                title="Kanban Settings"
              >
                <Settings size={16} />
              </ActionIcon>
            )}
          </>
        }
      />

      {loading ? (
        <div className="kanban-loading"><Loader /></div>
      ) : hasNoLists ? (
        <div className="kanban-empty-state">
          <div className="kanban-empty-content">
            <KanbanIcon size={64} style={{ color: 'var(--app-text-muted)', marginBottom: '16px' }} />
            <Title order={3} c="var(--app-text-secondary)" mb="sm">Welcome to Kanban Board!</Title>
            <Text c="var(--app-text-secondary)" size="lg" mb="xl" ta="center" style={{ maxWidth: '400px' }}>
              To get started with your Kanban board, create your first todo list from Todo Lists view.
            </Text>
            <Text c="var(--app-text-secondary)" size="sm" ta="center">
              💡 Once you have a list, you can organize your tasks into customizable columns here.
            </Text>
          </div>
        </div>
      ) : (
        <div className="kanban-content-area">
          <DndContext 
            sensors={sensors} 
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="kanban-scroll-area">
              {swimlaneMode === 'assignee' && swimlaneData ? (
                <div className="kanban-swimlanes-container">
                  {Object.entries(swimlaneData).map(([assignee, todos]) => {
                    const configuredColumnIds = new Set((config?.columns || []).map(c => c.id));
                    const swimlaneTodos = todos.filter(t => t.status && configuredColumnIds.has(t.status));
                    const unmappedForSwimlane = todos.filter(t => !t.status || !configuredColumnIds.has(t.status));

                    return (
                      <Swimlane
                        key={assignee}
                        assignee={assignee}
                        todos={swimlaneTodos}
                        unmappedTodos={unmappedForSwimlane}
                        columns={config?.columns || []}
                        onEditTodo={handleEditTodo}
                        listId={selectedListId}
                        refetchTodos={refetchTodos}
                        refetchConfig={refetch}
                        onMoveAllToStart={handleMoveAllToStart}
                        startColumnName={getStartColumnName()}
                        isCollapsed={!!collapsedSwimlanes[assignee]}
                        onToggleCollapse={() => toggleSwimlaneCollapse(assignee)}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="kanban-columns-container">
                  {/* Render unmapped column first if it has todos */}
                  {todosByColumn['__unmapped__']?.length > 0 && (
                    <UnmappedColumn 
                      todos={todosByColumn['__unmapped__']}
                      onEditTodo={handleEditTodo}
                      onMoveAllToStart={handleMoveAllToStart}
                      startColumnName={getStartColumnName()}
                    />
                  )}
                  
                  {/* Render configured columns */}
                  {config?.columns?.map((col: KanbanColumnType) => {
                    return (
                    <KanbanColumn
                      key={col.id}
                      id={col.id}
                      name={col.name}
                      color={col.color}
                      todos={(todosByColumn[col.id] || [])}
                      columnType={col.type}
                      onEditTodo={handleEditTodo}
                      listId={selectedListId || undefined}
                      onTaskCreated={async () => {
                        // Refetch both todos and kanban config to show the newly created task
                        await Promise.all([
                          refetchTodos(),
                          refetch()
                        ]);
                      }}
                    />
                    );
                  })}
                  
                  {/* Invisible spacer for right margin */}
                  <div style={{ minWidth: '16px', flexShrink: 0 }}></div>
                </div>
              )}
            </div>
            <DragOverlay>
              {draggedTodo ? (
                <div className="kanban-card-overlay">
                  <div className="kanban-card-title">
                    {draggedTodo.title}
                  </div>
                  <div className="kanban-card-meta">
                    {draggedTodo.priority !== 'Medium' && <Badge size="xs" variant="light" color={draggedTodo.priority === 'High' ? 'red' : 'gray'}>{draggedTodo.priority}</Badge>}
                    {draggedTodo.dueDate && <Badge size="xs" variant="outline" color="blue">{new Date(draggedTodo.dueDate).toLocaleDateString()}</Badge>}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Kanban Settings Modal */}
      {selectedListId && (
        <KanbanSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          listId={selectedListId}
          listName={lists.find(l => l.id === selectedListId)?.name || 'List'}
        />
      )}

  {/* Edit Todo Modal */}
  {editingTodo && (
        <Modal
          isOpen={!!editingTodo}
          onClose={handleCloseEdit}
          hideCloseButton={true}
          headerContent={
            <TaskTitleInput
              value={editingTodo.title}
              onChange={(value) => {
                setEditingTodo(prev => prev ? { ...prev, title: value } : null);
                setEditFormData(prev => prev ? { ...prev, title: value } : { ...editingTodo, title: value });
              }}
              autoFocus={true}
              isCompleted={editingTodo.isCompleted}
              onToggleComplete={() => {
                const newCompletedState = !editingTodo.isCompleted;
                // Update local state immediately
                setEditingTodo(prev => prev ? { ...prev, isCompleted: newCompletedState } : null);
                // Update in backend
                updateTodo.mutate({
                  id: editingTodo.id,
                  data: { isCompleted: newCompletedState }
                }, {
                  onSuccess: () => {
                    // Close modal after longer delay if marking as complete
                    if (newCompletedState) {
                      setTimeout(() => handleCloseEdit(), 600);
                    }
                  }
                });
              }}
            />
          }
          actionButtons={
            <>
              <button
                type="button"
                onClick={() => {
                  if (editFormData && editingTodo && editingTodo.id > 0) {
                    updateTodo.mutate({
                      id: editingTodo.id,
                      data: editFormData
                    }, {
                      onSuccess: () => {
                        handleCloseEdit();
                      },
                    });
                  }
                }}
                disabled={!editFormData || !editFormData.title?.trim() || updateTodo.isPending || (editingTodo && editingTodo.id < 0)}
                className="header-btn header-btn-save"
                title={editingTodo && editingTodo.id < 0 ? "Please wait for task to be created..." : "Save task"}
                aria-label="Save task"
              >
                <Check size={16} />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (editingTodo) {
                    deleteTodo.mutate(editingTodo.id, {
                      onSuccess: () => {
                        handleCloseEdit();
                      }
                    });
                  }
                }}
                disabled={deleteTodo.isPending}
                className="header-btn header-btn-delete"
                title="Delete task"
                aria-label="Delete task"
              >
                <Trash2 size={16} />
              </button>
              <button
                type="button"
                onClick={handleCloseEdit}
                className="header-btn header-btn-close"
                title="Cancel"
                aria-label="Cancel"
              >
                <X size={16} />
              </button>
            </>
          }
        >
          <EditTodoForm 
            todo={editingTodo}
            onComplete={handleCloseEdit}
            onDataChange={setEditFormData}
            renderTitleInline={false}
          />
        </Modal>
      )}
    </div>
  );
};
